/**
 * Account Preferences Page - Trade Loop v2.0
 *
 * Manage notification preferences and account settings
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { usePricing } from "@/hooks/usePricing";
import * as authService from "@/services/authService";
import { api } from "@/lib/api";
import { isNative } from "@/utils/platform";
import AppLayout from "@/components/layout/AppLayout";
import { AppVersionFooter } from '@/components/AppVersionFooter';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { PushNotificationManager } from "@/components/notifications/PushNotificationManager";
import {
  Bell,
  Mail,
  Lock,
  CreditCard,
  Crown,
  Calendar,
  DollarSign,
  Check,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Settings,
  FileText,
  Shield,
  CheckCircle2,
  XCircle,
  ChevronRight,
} from "lucide-react";

// Helper function to get card brand display info
const getCardBrandInfo = (brand: string | undefined) => {
  if (!brand) return { name: 'Card', logoUrl: null };

  const brandLower = brand.toLowerCase();

  // Using CDN-hosted card brand logos
  const brandMap: Record<string, { name: string; logoUrl: string }> = {
    'visa': {
      name: 'Visa',
      logoUrl: 'https://cdn.brandfetch.io/visa.com/w/400/h/400/logo?c=1dxbfHSJFAPEGdCLU4o5B'
    },
    'mastercard': {
      name: 'Mastercard',
      logoUrl: 'https://cdn.brandfetch.io/mastercard.com/w/400/h/400/logo?c=1dxbfHSJFAPEGdCLU4o5B'
    },
    'amex': {
      name: 'American Express',
      logoUrl: 'https://cdn.brandfetch.io/americanexpress.com/w/400/h/400/logo?c=1dxbfHSJFAPEGdCLU4o5B'
    },
    'discover': {
      name: 'Discover',
      logoUrl: 'https://cdn.brandfetch.io/discover.com/w/400/h/400/logo?c=1dxbfHSJFAPEGdCLU4o5B'
    },
    'diners': {
      name: 'Diners Club',
      logoUrl: 'https://cdn.brandfetch.io/dinersclub.com/w/400/h/400/logo?c=1dxbfHSJFAPEGdCLU4o5B'
    },
    'jcb': {
      name: 'JCB',
      logoUrl: 'https://cdn.brandfetch.io/jcb.com/w/400/h/400/logo?c=1dxbfHSJFAPEGdCLU4o5B'
    },
    'unionpay': {
      name: 'UnionPay',
      logoUrl: 'https://cdn.brandfetch.io/unionpay.com/w/400/h/400/logo?c=1dxbfHSJFAPEGdCLU4o5B'
    },
  };

  return brandMap[brandLower] || {
    name: brand.charAt(0).toUpperCase() + brand.slice(1),
    logoUrl: null
  };
};

export default function AccountPage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();
  const { getPlanPrice, getPlanPriceFormatted } = usePricing();
  const navigate = useNavigate();

  // Check if user is an organization member
  const isOrganizationMember = user?.organizationMembership?.role === 'member';

  // Email state
  const [newEmail, setNewEmail] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  // Password state
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Payment methods (will be populated when payment integration is added)
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Real subscription and invoice data
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [seatInfo, setSeatInfo] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Which auth identities are linked to this account (`email` / `google` / `apple`). Used to
  // hide the email/password edit rows for OAuth-only accounts, since those credentials live
  // with the identity provider and Trade Loop cannot change them. `null` means the fetch has
  // not resolved yet — we render optimistically (show the card) in that case so email/pass
  // users do not see a flash of hidden card on load.
  const [linkedProviders, setLinkedProviders] = useState<
    Array<'email' | 'google' | 'apple'> | null
  >(null);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { profile } = await api.get<{ profile: { linkedProviders?: Array<'email' | 'google' | 'apple'> } }>(
          '/api/auth/profile',
        );
        setLinkedProviders(profile?.linkedProviders ?? []);
      } catch (error) {
        console.error('Failed to fetch linked providers:', error);
        // Leave as null so the card renders — safer to show than to hide the wrong user's
        // credential controls.
      }
    })();
  }, [user?.id]);

  // While the fetch is in flight we assume email identity is present (optimistic render).
  // Once resolved, hide the card only if we know for sure the account has no email identity.
  const showAccountSecurityCard = linkedProviders === null || linkedProviders.includes('email');


  // Fetch subscription and invoice data
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;

      try {
        setIsLoadingData(true);

        // Fetch subscription details
        const subscriptionResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/subscriptions/current`, {
          credentials: 'include',
        });

        if (subscriptionResponse.ok) {
          const response = await subscriptionResponse.json();
          const subscription = response.data?.subscription || null;
          setSubscriptionData(subscription);

          // Fetch seat info if on Grow plan
          const plan = subscription?.plan?.toLowerCase() || user?.subscriptionPlan?.toLowerCase() || 'starter';
          if (plan === 'grow') {
            const seatsResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/organization/seats`, {
              credentials: 'include',
            });
            if (seatsResponse.ok) {
              const seatsData = await seatsResponse.json();
              setSeatInfo(seatsData.data);
            }
          }
        }

        // Fetch recent invoices (limit 5)
        const invoicesResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/billing/invoices?limit=5`, {
          credentials: 'include',
        });

        if (invoicesResponse.ok) {
          const response = await invoicesResponse.json();
          setInvoices(response.data?.invoices || []);
        }

        // Fetch payment methods
        const paymentMethodsResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods`, {
          credentials: 'include',
        });

        if (paymentMethodsResponse.ok) {
          const response = await paymentMethodsResponse.json();
          // Map API response to UI format
          const methods = (response.data?.paymentMethods || []).map((pm: any) => ({
            id: pm.id,
            brand: pm.cardBrand || pm.card_brand || 'Card',
            last4: pm.cardLastFour || pm.card_last_four || '****',
            expMonth: pm.cardExpMonth || pm.card_exp_month,
            expYear: pm.cardExpYear || pm.card_exp_year,
            isDefault: pm.isDefault || pm.is_default || false,
            nickname: pm.nickname,
          }));
          setPaymentMethods(methods);
        }
      } catch (error) {
        console.error('Failed to fetch account data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [isAuthenticated]);

  const handleUpdateEmail = async () => {
    if (!newEmail) {
      showError("Email Required", "Please enter a new email address");
      return;
    }

    if (!newEmail.includes("@")) {
      showError("Invalid Email", "Please enter a valid email address");
      return;
    }

    try {
      await authService.updateEmail(newEmail);
      showSuccess("Email Update Requested", "Please check your new email for a verification link");
      setIsEditingEmail(false);
      setNewEmail("");
    } catch (error: any) {
      showError("Update Failed", error.message || "Failed to update email");
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError("All Fields Required", "Please fill in all password fields");
      return;
    }

    if (newPassword.length < 8) {
      showError("Password Too Short", "Password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      showError("Passwords Don't Match", "New password and confirmation must match");
      return;
    }

    try {
      await authService.updatePassword(newPassword);
      showSuccess("Password Changed", "Your password has been updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setIsEditingPassword(false);
    } catch (error: any) {
      showError("Change Failed", error.message || "Failed to change password");
    }
  };

  // Password strength calculator
  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: "", color: "" };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 1) return { score: 1, label: "Weak", color: "bg-red-500" };
    if (score <= 2) return { score: 2, label: "Fair", color: "bg-orange-500" };
    if (score <= 3) return { score: 3, label: "Good", color: "bg-yellow-500" };
    if (score <= 4) return { score: 4, label: "Strong", color: "bg-green-500" };
    return { score: 5, label: "Very Strong", color: "bg-emerald-500" };
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  // Card 12 — NEVER on native. `/payment-methods` renders a Stripe CardElement and runs
  // confirmCardSetup, i.e. it takes a card inside the app. For a subscription that unlocks in-app
  // features that is a Google Play Billing violation and an Apple Guideline 3.1.1 violation. All
  // billing is handled on gotradeloop.com (owner directive, 2026-08-25).
  //
  // Card 18 guarded the checkout paths (CheckoutButton, PlansPage, VerificationSection) and this
  // entry point was missed — it is the only remaining surface that puts a card form in the app.
  // The route itself is guarded too (App.tsx), so this is defence in depth rather than the fix.
  const handleAddPaymentMethod = () => {
    if (isNative) return;
    navigate("/payment-methods");
  };

  const handleRemovePaymentMethod = (methodId: string) => {
    showWarning("Remove Payment Method", "Are you sure you want to remove this payment method?");
    // TODO: Implement payment method removal
  };

  if (isLoading) {
    return (
      <AppLayout activeTab="profile" title="Account" backLink="/settings">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading preferences...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activeTab="profile" title="Account" backLink="/settings">
      <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto px-4 pb-[25px] md:pb-0 space-y-6">
        {/* Push Notifications Card */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                <Bell className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <span>Push Notifications</span>
                <p className="text-sm font-normal text-gray-600 mt-0.5">
                  Stay updated with real-time notifications
                </p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <PushNotificationManager />
          </CardContent>
        </Card>

        {/* Account Security — hidden for OAuth-only accounts. The email and password rows
            both edit credentials Trade Loop owns; for a Google-only or Apple-only account,
            those credentials live with the identity provider and this app cannot change them.
            An account merged with an email/password identity (linkedProviders includes 'email')
            still sees the card so it can edit that email identity, regardless of which
            provider was used to sign in this session. */}
        {showAccountSecurityCard && (
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center">
                <Shield className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <span className="block">Account Security</span>
                <span className="text-sm font-normal text-gray-500">Manage your login credentials</span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-gray-100">
            {/* Email Address Row */}
            <div className="p-5">
              <div
                className={`flex items-center justify-between ${!isEditingEmail ? 'cursor-pointer hover:bg-gray-50 -m-5 p-5 transition-colors' : ''}`}
                onClick={() => !isEditingEmail && setIsEditingEmail(true)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Email Address</h4>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                </div>
                {!isEditingEmail && (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>

              {isEditingEmail && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      New Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter new email address"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="max-w-md"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleUpdateEmail}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Update Email
                    </Button>
                    <Button
                      onClick={() => {
                        setIsEditingEmail(false);
                        setNewEmail("");
                      }}
                      variant="ghost"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    You'll receive a verification email at your new address to confirm the change.
                  </p>
                </div>
              )}
            </div>

            {/* Password Row */}
            <div className="p-5">
              <div
                className={`flex items-center justify-between ${!isEditingPassword ? 'cursor-pointer hover:bg-gray-50 -m-5 p-5 transition-colors' : ''}`}
                onClick={() => !isEditingPassword && setIsEditingPassword(true)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                    <Lock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Password</h4>
                    <p className="text-sm text-gray-500">••••••••••••</p>
                  </div>
                </div>
                {!isEditingPassword && (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>

              {isEditingPassword && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-4 max-w-md">
                  {/* Current Password */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Current Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      New Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {/* Password Strength Indicator */}
                    {newPassword && (
                      <div className="mt-2 space-y-1.5">
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((level) => (
                            <div
                              key={level}
                              className={`h-1.5 flex-1 rounded-full transition-colors ${
                                level <= passwordStrength.score ? passwordStrength.color : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <p className={`text-xs font-medium ${
                          passwordStrength.score <= 1 ? 'text-red-600' :
                          passwordStrength.score <= 2 ? 'text-orange-600' :
                          passwordStrength.score <= 3 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {passwordStrength.label}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`pr-10 ${
                          confirmPassword && (passwordsMatch ? 'border-green-500 focus:ring-green-500' : 'border-red-500 focus:ring-red-500')
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {confirmPassword && (
                      <p className={`text-xs mt-1.5 flex items-center gap-1 ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                        {passwordsMatch ? (
                          <><CheckCircle2 className="w-3.5 h-3.5" /> Passwords match</>
                        ) : (
                          <><XCircle className="w-3.5 h-3.5" /> Passwords don't match</>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Password Requirements */}
                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                    <p className="font-medium mb-1.5">Password requirements:</p>
                    <ul className="space-y-1">
                      <li className={`flex items-center gap-1.5 ${newPassword.length >= 8 ? 'text-green-600' : ''}`}>
                        {newPassword.length >= 8 ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-gray-300 inline-block" />}
                        At least 8 characters
                      </li>
                      <li className={`flex items-center gap-1.5 ${/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) ? 'text-green-600' : ''}`}>
                        {/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-gray-300 inline-block" />}
                        Upper and lowercase letters
                      </li>
                      <li className={`flex items-center gap-1.5 ${/\d/.test(newPassword) ? 'text-green-600' : ''}`}>
                        {/\d/.test(newPassword) ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-gray-300 inline-block" />}
                        At least one number
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <Button
                      onClick={handleChangePassword}
                      size="sm"
                      disabled={!currentPassword || !newPassword || !confirmPassword || !passwordsMatch || newPassword.length < 8}
                      className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                    >
                      Update Password
                    </Button>
                    <Button
                      onClick={() => {
                        setIsEditingPassword(false);
                        setCurrentPassword("");
                        setNewPassword("");
                        setConfirmPassword("");
                      }}
                      variant="ghost"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Payment Methods - Hidden for organization members */}
        {!isOrganizationMember && (
          <Card>
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-green-600" />
                </div>
                <span>Payment Methods</span>
              </CardTitle>
            </CardHeader>
          <CardContent className="p-6 space-y-4">
            {isLoadingData ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="mt-2 text-sm text-gray-500">Loading payment methods...</p>
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No payment methods added</p>
                {/* Plain, NON-TAPPABLE text on native. A tappable link out to billing is itself
                    steering, and Card 18 deliberately made the Android checkout copy-only. */}
                {isNative ? (
                  <p className="text-sm text-gray-500">
                    Payment methods are managed on our website.
                  </p>
                ) : (
                  <Button
                    onClick={handleAddPaymentMethod}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Payment Method
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map((method) => {
                  const brandInfo = getCardBrandInfo(method.brand);
                  return (
                    <div
                      key={method.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50/30 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-10 bg-white border border-gray-200 rounded flex items-center justify-center p-1.5 shadow-sm">
                          {brandInfo.logoUrl ? (
                            <img
                              src={brandInfo.logoUrl}
                              alt={brandInfo.name}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <CreditCard className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">
                              {method.nickname || `${brandInfo.name} •••• ${method.last4}`}
                            </p>
                            {method.isDefault && (
                              <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {method.nickname && `${brandInfo.name} •••• ${method.last4} • `}
                            {method.expMonth && method.expYear ? `Expires ${method.expMonth}/${method.expYear}` : 'No expiration date'}
                          </p>
                        </div>
                      </div>
                      {!method.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePaymentMethod(method.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
                {!isNative && (
                  <Button
                    onClick={handleAddPaymentMethod}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Payment Method
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Subscription Details - Hidden for organization members */}
        {!isOrganizationMember && (
          <Card>
            <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b">
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center">
                  <Crown className="h-5 w-5 text-orange-600" />
                </div>
                <span>Subscription</span>
              </CardTitle>
            </CardHeader>
          <CardContent className="p-6 space-y-4">
            {isLoadingData ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="mt-2 text-sm text-gray-500">Loading subscription details...</p>
              </div>
            ) : (
              <>
                {/* Current Plan */}
                <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl space-y-4">
                  {/* Plan Name and Status */}
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">
                      {user?.subscriptionPlan || 'Starter'} Plan
                    </h3>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${
                        subscriptionData?.status === "active"
                          ? "bg-green-100 text-green-700"
                          : subscriptionData?.status === "canceled"
                          ? "bg-gray-100 text-gray-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {subscriptionData?.status || 'active'}
                    </span>
                  </div>

                  {/* Price with Seat Breakdown */}
                  <div>
                    {subscriptionData?.status === 'trialing' ? (
                      <p className="text-sm text-gray-600">Free for 30 days</p>
                    ) : (
                      <>
                        {(() => {
                          const currentPlan = subscriptionData?.plan || user?.subscriptionPlan?.toLowerCase() || 'starter';
                          const basePlanAmount = currentPlan === 'starter' ? 0 : getPlanPrice(currentPlan as 'pro' | 'grow');
                          const additionalSeatCost = (seatInfo?.additional || 0) * (seatInfo?.costPerAdditional || 5);
                          const totalBillingAmount = basePlanAmount + additionalSeatCost;

                          return (
                            <div>
                              <p className="text-2xl font-bold text-gray-900">
                                {totalBillingAmount === 0 ? 'Free' : `$${totalBillingAmount}/month`}
                              </p>
                              {seatInfo && seatInfo.additional > 0 && (
                                <p className="text-xs text-gray-600 mt-1">
                                  ${basePlanAmount} plan + ${additionalSeatCost} ({seatInfo.additional} seat{seatInfo.additional > 1 ? 's' : ''})
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>

                  {/* Buttons at Bottom */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() => navigate("/subscription")}
                      variant="outline"
                      className="w-full sm:w-1/2 border-orange-300 hover:bg-orange-100"
                    >
                      View Details
                    </Button>
                    <Button
                      onClick={() => navigate("/plans")}
                      className="w-full sm:w-1/2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                    >
                      Change Plan
                    </Button>
                  </div>
                </div>

                {/* Billing Details */}
                {subscriptionData && subscriptionData.plan !== 'starter' && (
                  <div className={`grid grid-cols-1 ${subscriptionData.status !== 'trialing' ? 'md:grid-cols-2' : ''} gap-4`}>
                    <div className="p-4 border border-gray-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <p className="text-sm font-medium text-gray-700">
                          {subscriptionData.status === 'trialing' ? 'Trial Ends' : 'Next Billing Date'}
                        </p>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">
                        {subscriptionData.currentPeriodEnd
                          ? new Date(subscriptionData.currentPeriodEnd).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })
                          : 'N/A'}
                      </p>
                      {subscriptionData.status === 'trialing' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Add a payment method to continue after trial
                        </p>
                      )}
                    </div>

                    {subscriptionData.status !== 'trialing' && (
                      <div className="p-4 border border-gray-200 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-4 h-4 text-gray-500" />
                          <p className="text-sm font-medium text-gray-700">Next Payment</p>
                        </div>
                        <p className="text-lg font-semibold text-gray-900">
                          ${(() => {
                            const basePlanAmount = subscriptionData.plan === 'grow' ? getPlanPrice('grow') : subscriptionData.plan === 'pro' ? getPlanPrice('pro') : 0;
                            const additionalSeatCost = (seatInfo?.additional || 0) * (seatInfo?.costPerAdditional || 5);
                            return (basePlanAmount + additionalSeatCost).toFixed(2);
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Invoices - Hidden for organization members */}
        {!isOrganizationMember && (
          <Card>
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl flex items-center justify-center">
                <FileText className="h-5 w-5 text-indigo-600" />
              </div>
              <span>Invoices</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {isLoadingData ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="mt-2 text-sm text-gray-500">Loading invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No invoices yet</p>
                <p className="text-sm text-gray-400 mt-1">Invoices will appear here once you subscribe to a paid plan</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50/30 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            invoice.status === "paid"
                              ? "bg-green-100"
                              : invoice.status === "open"
                              ? "bg-blue-100"
                              : "bg-gray-100"
                          }`}
                        >
                          {invoice.status === "paid" ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {invoice.periodStart && invoice.periodEnd
                              ? `${new Date(invoice.periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(invoice.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                              : 'Invoice'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(invoice.createdAt).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          ${((invoice.amountPaid || invoice.amountDue) / 100).toFixed(2)}
                        </p>
                        <span
                          className={`text-xs font-medium ${
                            invoice.status === "paid"
                              ? "text-green-600"
                              : invoice.status === "open"
                              ? "text-blue-600"
                              : "text-gray-600"
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => navigate("/subscription")}
                  variant="outline"
                  className="w-full mt-4 border-indigo-300 hover:bg-indigo-50"
                >
                  View All Invoices
                </Button>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Card 13 — which native binary and which OTA web bundle this device is running.
            Ask a tester to read this line rather than inferring it from behaviour. */}
        <AppVersionFooter />
      </div>
    </AppLayout>
  );
}
